import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Field, Input, Section, Select } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import type { HelpKey } from '@zt/help';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { usePermissions } from '../lib/session';
import type { Store } from '../lib/types';

/**
 * Dışa aktarma (F3B-19): sipariş, sipariş satırı, müşteri, stok, fatura ve ödeme listeleri. Mali müşavire
 * gönderilecek dosyalar buradan alınır. Kişisel veriler varsayılan olarak maskelidir.
 */

const PERIODS = ['this_month', 'last_month', 'last_30', 'this_year', 'last_year', 'custom', 'all'] as const;
type Period = (typeof PERIODS)[number];

type Kind = 'orders' | 'order_lines' | 'customers' | 'stock' | 'invoices' | 'payments';
interface ListDef {
  kind: Kind;
  permission: string;
  help: HelpKey;
  /** Dönem ve mağaza süzgeci uygulanır mı? */
  dated: boolean;
  byStore: boolean;
  /** Kişisel veri içerir (maskeli/açık seçimi uygulanır). */
  personal: boolean;
}

const LISTS: ListDef[] = [
  {
    kind: 'orders',
    permission: 'orders.order.read',
    help: 'exports.orders',
    dated: true,
    byStore: true,
    personal: true,
  },
  {
    kind: 'order_lines',
    permission: 'orders.order.read',
    help: 'exports.order_lines',
    dated: true,
    byStore: true,
    personal: false,
  },
  {
    kind: 'customers',
    permission: 'customers.customer.read',
    help: 'exports.customers',
    dated: true,
    byStore: false,
    personal: true,
  },
  {
    kind: 'stock',
    permission: 'inventory.stock.read',
    help: 'exports.stock',
    dated: false,
    byStore: false,
    personal: false,
  },
  {
    kind: 'invoices',
    permission: 'invoices.invoice.read',
    help: 'exports.invoices',
    dated: true,
    byStore: true,
    personal: true,
  },
  {
    kind: 'payments',
    permission: 'payments.payment.read',
    help: 'exports.payments',
    dated: true,
    byStore: true,
    personal: true,
  },
];

export const EXPORT_PERMISSIONS = [...new Set(LISTS.map((l) => l.permission))];

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Seçilen dönemin gün aralığı (tarayıcının takvim günü; sunucu Türkiye saatiyle yorumlar). */
export function periodRange(p: Period, today = new Date()): { from?: string; to?: string } {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (p) {
    case 'this_month':
      return { from: ymd(new Date(y, m, 1)), to: ymd(today) };
    case 'last_month':
      return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case 'last_30':
      return { from: ymd(new Date(y, m, today.getDate() - 29)), to: ymd(today) };
    case 'this_year':
      return { from: ymd(new Date(y, 0, 1)), to: ymd(today) };
    case 'last_year':
      return { from: ymd(new Date(y - 1, 0, 1)), to: ymd(new Date(y - 1, 11, 31)) };
    default:
      return {};
  }
}

export function ExportsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [period, setPeriod] = useState<Period>('last_month');
  const [custom, setCustom] = useState(() => periodRange('this_month'));
  const [storeId, setStoreId] = useState('');
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [personal, setPersonal] = useState<'masked' | 'full'>('masked');
  const [busy, setBusy] = useState<Kind | null>(null);
  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => api.get<Store[]>('/v1/admin/stores'),
    enabled: can('org.store.read'),
  });
  const lists = LISTS.filter((l) => can(l.permission));
  const range = period === 'custom' ? custom : periodRange(period);
  const invalid = period === 'custom' && !!range.from && !!range.to && range.from > range.to;
  const day = (d: string) => formatDate(`${d}T12:00:00`, false);

  const download = async (l: ListDef) => {
    const qs = new URLSearchParams({ format });
    if (l.dated) {
      if (range.from) qs.set('from', range.from);
      if (range.to) qs.set('to', range.to);
    }
    if (l.byStore && storeId) qs.set('storeId', storeId);
    if (l.personal && personal === 'full') qs.set('personalData', 'full');
    if (l.kind === 'order_lines') qs.set('level', 'lines');
    const path = l.kind === 'order_lines' ? 'orders' : l.kind;
    setBusy(l.kind);
    try {
      const { blob, fileName } = await api.blob(`/v1/admin/exports/${path}?${qs.toString()}`);
      const name = fileName ?? `${path}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('admin.exports.downloaded', { name }));
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'step_up_required'))
        toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageFrame helpKey="page.exports">
      <Section title={t('admin.exports.settingsTitle')} description={t('admin.exports.settingsLead')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.exports.period')} help={<HelpTip k="exports.period" />}>
            {(p) => (
              <Select {...p} value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                {PERIODS.map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.exports.periods.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {(stores.data?.length ?? 0) > 1 && (
            <Field label={t('admin.exports.store')} help={<HelpTip k="exports.store" />}>
              {(p) => (
                <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                  <option value="">{t('admin.exports.allStores')}</option>
                  {stores.data!.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {period === 'custom' && (
            <>
              <Field label={t('admin.exports.from')} help={<HelpTip k="exports.from" />}>
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    value={custom.from ?? ''}
                    onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value || undefined }))}
                  />
                )}
              </Field>
              <Field
                label={t('admin.exports.to')}
                help={<HelpTip k="exports.to" />}
                error={invalid ? t('errors.date_range_invalid') : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    value={custom.to ?? ''}
                    onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value || undefined }))}
                  />
                )}
              </Field>
            </>
          )}
          <Field label={t('admin.exports.format')} help={<HelpTip k="exports.format" />}>
            {(p) => (
              <Select {...p} value={format} onChange={(e) => setFormat(e.target.value as 'xlsx' | 'csv')}>
                <option value="xlsx">{t('admin.exports.formats.xlsx')}</option>
                <option value="csv">{t('admin.exports.formats.csv')}</option>
              </Select>
            )}
          </Field>
          {can('customers.pii.read') && (
            <Field
              label={t('admin.exports.personalData')}
              description={t('admin.exports.personalDataHint')}
              help={<HelpTip k="exports.personal_data" />}
            >
              {(p) => (
                <Select
                  {...p}
                  value={personal}
                  onChange={(e) => setPersonal(e.target.value as 'masked' | 'full')}
                >
                  <option value="masked">{t('admin.exports.personalDataModes.masked')}</option>
                  <option value="full">{t('admin.exports.personalDataModes.full')}</option>
                </Select>
              )}
            </Field>
          )}
        </div>
        <p className="mt-4 text-sm text-ink-2" aria-live="polite">
          {range.from || range.to
            ? t('admin.exports.range', {
                from: range.from ? day(range.from) : '…',
                to: range.to ? day(range.to) : '…',
              })
            : t('admin.exports.rangeAll')}
        </p>
      </Section>

      <Section title={t('admin.exports.listsTitle')} description={t('admin.exports.listsLead')}>
        {lists.length === 0 ? (
          <p className="text-sm text-ink-2">{t('admin.exports.noAccess')}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface" role="list">
            {lists.map((l) => (
              <li key={l.kind} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{t(`admin.exports.kinds.${l.kind}.title`)}</p>
                  <p className="text-sm text-ink-2">
                    {t(`admin.exports.kinds.${l.kind}.lead`)}
                    {l.kind === 'stock' && ` ${t('admin.exports.stockNote')}`}
                    {l.kind === 'customers' && ` ${t('admin.exports.customersNote')}`}
                  </p>
                </div>
                <span className="flex items-center gap-1">
                  <Button
                    size="sm"
                    loading={busy === l.kind}
                    disabled={(busy !== null && busy !== l.kind) || (l.dated && invalid)}
                    onClick={() => void download(l)}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    {t(`admin.exports.kinds.${l.kind}.action`)}
                  </Button>
                  <HelpTip k={l.help} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </PageFrame>
  );
}
